import { ApiError, type Reseller } from '@central/api';
import { Card, PageHeader, color, formatDate } from '@central/ui';
import { Alert, Button, FileInput, Group, Modal, Table, TextInput } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import { useCanWrite } from '../lib/me';

const errors = {
  duplicate: 'A reseller with that name already exists.',
  in_use: 'Move this reseller’s customers and users to another reseller (or to Rethink) first.',
};

/** The logo types the server accepts; it sniffs the bytes, this only saves a round trip. */
const logoTypes = ['image/png', 'image/jpeg', 'image/webp'];
/** Largest logo the server accepts, in bytes. */
export const maxLogoBytes = 256 * 1024;

const fieldText = {
  name: 'Enter the reseller’s name (at most 120 characters).',
  supportEmail: 'Enter an email address, like support@partner.example.',
  supportPhone: 'At most 40 characters, on one line.',
  website: 'Enter an https:// address.',
  logo: 'Choose a PNG, JPEG or WebP image of at most 256 KB.',
};

/**
 * Resellers: the partners that install GoATMS and give tier 1 support to
 * the customers assigned to them. A customer is assigned on its page; a
 * reseller's engineers are staff with the Reseller role.
 */
export function Resellers() {
  const api = useApi();
  const canWrite = useCanWrite();
  const [editing, setEditing] = useState<Reseller | 'new'>();
  const list = useQuery({ queryKey: keys.resellers, queryFn: () => api.resellers.list() });

  return (
    <>
      <PageHeader
        title="Resellers"
        subtitle="Partners give tier 1 support to their customers and escalate to Rethink"
        actions={
          canWrite && (
            <Button color="dark" size="sm" h={40} onClick={() => setEditing('new')}>
              New reseller
            </Button>
          )
        }
      />
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            list.data!.items.length === 0 ? (
              <Empty>No resellers yet. Every customer is supported by Rethink directly.</Empty>
            ) : (
              <DataTable
                label="Resellers"
                minWidth={780}
                head={[
                  'Name',
                  'Support contact',
                  'Customers',
                  'Users',
                  'Admins',
                  'Added',
                  ...(canWrite ? [''] : []),
                ]}
              >
                {list.data!.items.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td style={{ fontWeight: 600 }}>
                      <Group gap={8} wrap="nowrap">
                        {r.logoUrl && <LogoImage src={r.logoUrl} name={r.name} size={24} />}
                        <Link to="/resellers/$resellerId" params={{ resellerId: r.id }}>
                          {r.name}
                        </Link>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ fontSize: 13 }}>
                      {[r.supportEmail, r.supportPhone, r.website].filter(Boolean).join(' · ') || (
                        <span style={muted}>–</span>
                      )}
                    </Table.Td>
                    <Table.Td>{r.customers}</Table.Td>
                    <Table.Td>{r.users}</Table.Td>
                    <Table.Td>{r.admins}</Table.Td>
                    <Table.Td style={muted}>{formatDate(r.createdAt)}</Table.Td>
                    {canWrite && (
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          aria-label={`Edit ${r.name}`}
                          onClick={() => setEditing(r)}
                        >
                          Edit
                        </Button>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
        <p style={{ margin: 0, padding: '12px 20px', fontSize: 13, ...muted }}>
          Open a reseller to see its customers, team and support load, assign customers to it and
          invite its engineers. Reseller users see only their reseller’s customers, their dedicated
          environments, support conversations and release installers, but never licenses. The
          support contact and logo are shown to the reseller’s customers in the portal.
        </p>
      </Card>
      {canWrite && editing && (
        <EditReseller
          key={editing === 'new' ? 'new' : editing.id}
          r={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
        />
      )}
    </>
  );
}

/** A reseller's logo, contained in a square of size pixels. */
export function LogoImage({ src, name, size }: { src: string; name: string; size: number }) {
  return (
    <img
      src={src}
      alt={`${name} logo`}
      width={size}
      height={size}
      style={{ objectFit: 'contain', flexShrink: 0 }}
    />
  );
}

/** A data: URL of file for previewing it (the CSP allows data: images, not blob:). */
function useDataUrl(file: File | null): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!file) {
      setUrl(undefined);
      return;
    }
    let live = true;
    const reader = new FileReader();
    reader.onload = () => {
      if (live && typeof reader.result === 'string') setUrl(reader.result);
    };
    reader.readAsDataURL(file);
    return () => {
      live = false;
    };
  }, [file]);
  return url;
}

function logoProblem(file: File | null): string | undefined {
  if (!file) return undefined;
  if (!logoTypes.includes(file.type) || file.size > maxLogoBytes) return fieldText.logo;
  return undefined;
}

/**
 * Create a reseller, or edit or delete `r`: its name, support contact and
 * logo. A new reseller opens its page; a deleted one returns to the list.
 */
export function EditReseller({ r, onClose }: { r?: Reseller; onClose: () => void }) {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(r?.name ?? '');
  const [supportEmail, setSupportEmail] = useState(r?.supportEmail ?? '');
  const [supportPhone, setSupportPhone] = useState(r?.supportPhone ?? '');
  const [website, setWebsite] = useState(r?.website ?? '');
  const [logo, setLogo] = useState<File | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [problems, setProblems] = useState<Record<string, string | undefined>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const preview = useDataUrl(logo);
  const done = async () => {
    await qc.invalidateQueries({ queryKey: keys.resellers });
    onClose();
  };
  const save = useMutation({
    mutationFn: async () => {
      const contact = {
        supportEmail: supportEmail.trim(),
        supportPhone: supportPhone.trim(),
        website: website.trim(),
      };
      const saved = r
        ? await api.resellers.update(r.id, { name: name.trim(), ...contact })
        : await api.resellers
            .create({ name: name.trim() })
            .then((c) =>
              Object.values(contact).some(Boolean) ? api.resellers.update(c.id, contact) : c,
            );
      if (logo) await api.resellers.setLogo(saved.id, logo);
      else if (removeLogo && r?.logoUrl) await api.resellers.deleteLogo(saved.id);
      return saved;
    },
    onSuccess: async (saved) => {
      await done();
      if (!r) await navigate({ to: '/resellers/$resellerId', params: { resellerId: saved.id } });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.fields)
        setProblems(
          Object.fromEntries(
            Object.entries(e.fields).map(([k, v]) => [
              k,
              (fieldText as Record<string, string | undefined>)[k] ?? v,
            ]),
          ),
        );
    },
  });
  const remove = useMutation({
    mutationFn: () => api.resellers.delete(r!.id),
    onSuccess: async () => {
      // Leave its page first, so nothing refetches the deleted reseller.
      await navigate({ to: '/resellers' });
      qc.removeQueries({ queryKey: keys.reseller(r!.id) });
      await done();
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const p: Record<string, string> = {};
    if (!name.trim()) p.name = 'Enter the reseller’s name.';
    const w = website.trim();
    if (w && !w.startsWith('https://')) p.website = fieldText.website;
    const l = logoProblem(logo);
    if (l) p.logo = l;
    setProblems(p);
    if (Object.keys(p).length === 0) save.mutate();
  };
  const error = save.error ?? remove.error;
  const fieldError = error instanceof ApiError && error.fields && Object.keys(error.fields).length;
  const shownLogo = preview ?? (removeLogo ? undefined : r?.logoUrl);

  return (
    <Modal opened onClose={onClose} title={r ? r.name : 'New reseller'}>
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Name"
          data-autofocus
          value={name}
          error={problems.name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <TextInput
          label="Support email"
          type="email"
          placeholder="support@partner.example"
          value={supportEmail}
          error={problems.supportEmail}
          onChange={(e) => setSupportEmail(e.currentTarget.value)}
        />
        <TextInput
          label="Support phone"
          value={supportPhone}
          maxLength={40}
          error={problems.supportPhone}
          onChange={(e) => setSupportPhone(e.currentTarget.value)}
        />
        <TextInput
          label="Website"
          placeholder="https://partner.example/support"
          value={website}
          error={problems.website}
          onChange={(e) => setWebsite(e.currentTarget.value)}
        />
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <FileInput
            label="Logo"
            description="PNG, JPEG or WebP, at most 256 KB"
            placeholder={r?.logoUrl && !removeLogo ? 'Replace logo' : 'Choose an image'}
            accept={logoTypes.join(',')}
            clearable
            value={logo}
            error={problems.logo}
            onChange={(f) => {
              setLogo(f);
              setProblems((p) => ({ ...p, logo: undefined }));
            }}
            style={{ flex: 1 }}
          />
          {shownLogo && (
            <div
              style={{
                border: `1px solid ${color.border}`,
                borderRadius: 6,
                padding: 4,
                lineHeight: 0,
              }}
            >
              <LogoImage src={shownLogo} name={name || 'Reseller'} size={56} />
            </div>
          )}
        </div>
        {r?.logoUrl && !logo && (
          <Button
            variant="subtle"
            size="compact-sm"
            color={removeLogo ? undefined : 'red'}
            style={{ alignSelf: 'flex-start' }}
            onClick={() => setRemoveLogo(!removeLogo)}
          >
            {removeLogo ? 'Keep logo' : 'Remove logo'}
          </Button>
        )}
        {error && !fieldError && <Alert color="red">{errorMessage(error, errors)}</Alert>}
        <Group justify={r ? 'space-between' : 'flex-end'}>
          {r &&
            (confirmDelete ? (
              <Group gap={8}>
                <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
                  Delete {r.name}
                </Button>
                <Button variant="default" onClick={() => setConfirmDelete(false)}>
                  Keep
                </Button>
              </Group>
            ) : (
              <Button variant="subtle" color="red" onClick={() => setConfirmDelete(true)}>
                Delete reseller
              </Button>
            ))}
          <Group gap={8}>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="dark" loading={save.isPending}>
              {r ? 'Save' : 'Create reseller'}
            </Button>
          </Group>
        </Group>
      </form>
    </Modal>
  );
}
